/**
 * WebSocket 服务模块
 * 连接到 wsBaseURL 定义的 WebSocket 服务器
 */

import { wsBaseURL } from '../config';
import store from '../state/store';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000; // 3秒
    this.listeners = new Map();
    this.isConnecting = false;
    this.isConnected = false;
  }

  /**
   * 连接到 WebSocket 服务器
   */
  connect() {
    if (this.isConnecting || this.isConnected) {
      console.log('WebSocket 已经在连接或已连接');
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.isConnecting = true;
      
      try {
        let url = wsBaseURL + '?token=' + store.getState().user["refresh-token"];
        console.log(`正在连接 WebSocket: ${url}`);
        this.socket = new WebSocket(url);

        this.socket.onopen = (event) => {
          console.log('WebSocket 连接成功');
          this.isConnecting = false;
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.emit('connected', event);
          resolve();
        };

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('收到 WebSocket 消息:', data);
            this.emit('message', data);
            
            // 根据消息类型分发事件
            if (data.type) {
              this.emit(data.type, data);
            }
          } catch (error) {
            console.error('解析 WebSocket 消息失败:', error, event.data);
            this.emit('rawmessage', event.data);
          }
        };

        this.socket.onerror = (error) => {
          console.error('WebSocket 错误:', error);
          this.isConnecting = false;
          this.emit('error', error);
          reject(error);
        };

        this.socket.onclose = (event) => {
          console.log(`WebSocket 连接关闭，代码: ${event.code}, 原因: ${event.reason}`);
          this.isConnecting = false;
          this.isConnected = false;
          this.emit('disconnected', event);
          
          // 尝试重新连接
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`尝试重新连接 (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
            setTimeout(() => this.connect(), this.reconnectInterval);
          } else {
            console.log('达到最大重连次数，停止重连');
          }
        };
      } catch (error) {
        this.isConnecting = false;
        console.error('创建 WebSocket 连接失败:', error);
        reject(error);
      }
    });
  }

  /**
   * 发送消息到服务器
   * @param {Object|string} data 要发送的数据
   */
  send(data) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('WebSocket 未连接，无法发送消息');
      return false;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.socket.send(message);
      return true;
    } catch (error) {
      console.error('发送 WebSocket 消息失败:', error);
      return false;
    }
  }

  /**
   * 关闭 WebSocket 连接
   */
  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  /**
   * 添加事件监听器
   * @param {string} event 事件名称
   * @param {Function} callback 回调函数
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * 移除事件监听器
   * @param {string} event 事件名称
   * @param {Function} callback 回调函数
   */
  off(event, callback) {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  /**
   * 触发事件
   * @param {string} event 事件名称
   * @param {any} data 事件数据
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`执行 WebSocket 事件 ${event} 的回调失败:`, error);
        }
      });
    }
  }

  /**
   * 获取连接状态
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      url: wsBaseURL
    };
  }
}

// 创建单例实例
const webSocketService = new WebSocketService();

export default webSocketService;